import { API_BASE_URL } from '@/config';
import { Loader2, RefreshCw, Trophy } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface TVLeaderboardData {
  workout: { id: string; name: string; scoring_type: string };
  affiliate_name: string;
  rankings: Array<{ rank: number; name: string; score: number; is_tie: boolean }>;
  refresh_interval_seconds: number;
}

function formatScore(value: number, scoringType: string): string {
  if (scoringType === 'time') {
    const mins = Math.floor(value / 60);
    const secs = value % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return String(value);
}

function getRankMedal(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return String(rank);
}

export default function TVLeaderboard() {
  const [searchParams] = useSearchParams();
  const workoutId = searchParams.get('workout_id');
  const affiliateId = searchParams.get('affiliate_id');

  const [data, setData] = useState<TVLeaderboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchLeaderboard = useCallback(async () => {
    if (!workoutId || !affiliateId) {
      setError('Missing workout_id or affiliate_id parameters');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/public/tv/leaderboard?workout_id=${workoutId}&affiliate_id=${affiliateId}`
      );
      const json = await response.json();
      if (json.success && json.data) {
        setData(json.data);
        setError(null);
      } else {
        setError(json.error || 'Failed to load leaderboard');
      }
    } catch (err) {
      console.error('Failed to fetch TV leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setIsLoading(false);
      setLastRefresh(new Date());
    }
  }, [workoutId, affiliateId]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchLeaderboard, 30000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#FFBB38]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">
        <div className="text-center">
          <img
            src="/ergshoot-logo.png"
            alt="ErgShoot"
            className="h-16 w-auto mx-auto mb-4 opacity-50"
          />
          <h1 className="text-2xl font-bold mb-2">Leaderboard Not Found</h1>
          <p className="text-gray-400">{error || 'No leaderboard data available.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col p-8 lg:p-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-12 w-auto" />
          <div>
            <h1 className="text-2xl font-bold text-[#FFBB38]">{data.affiliate_name}</h1>
            <p className="text-gray-400 text-lg">{data.workout.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <RefreshCw className="h-4 w-4" />
          <span>
            Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>

      {/* Leaderboard Title */}
      <div className="flex items-center gap-3 mb-8">
        <Trophy className="h-8 w-8 text-[#FFBB38]" />
        <h2 className="text-4xl lg:text-5xl font-bold">Leaderboard</h2>
      </div>

      {/* Rankings */}
      {data.rankings.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <Trophy className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-2xl">No scores yet</p>
            <p className="text-gray-600 mt-2">Be the first to log a score!</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3 max-w-3xl w-full mx-auto">
          {data.rankings.map((entry, index) => (
            <div
              key={`${entry.name}-${index}`}
              className={`flex items-center justify-between px-6 py-4 rounded-xl border transition-all ${
                entry.rank === 1
                  ? 'bg-[#FFBB38]/10 border-[#FFBB38]/40 shadow-lg shadow-[#FFBB38]/10'
                  : entry.rank === 2
                    ? 'bg-gray-400/5 border-gray-400/20'
                    : entry.rank === 3
                      ? 'bg-amber-900/10 border-amber-700/20'
                      : 'bg-white/5 border-white/10'
              }`}
            >
              {/* Rank */}
              <div className="flex items-center gap-4 min-w-[4rem]">
                <span
                  className={`text-2xl lg:text-3xl font-bold ${
                    entry.rank <= 3 ? 'text-2xl' : 'text-xl text-gray-400'
                  }`}
                >
                  {entry.rank <= 3 ? getRankMedal(entry.rank) : `#${entry.rank}`}
                </span>
                {entry.is_tie && (
                  <span className="text-xs text-gray-500 border border-gray-600 px-1 rounded">
                    TIE
                  </span>
                )}
              </div>

              {/* Name */}
              <div className="flex-1 px-4">
                <span
                  className={`font-semibold ${
                    entry.rank === 1
                      ? 'text-3xl lg:text-4xl text-white'
                      : entry.rank <= 3
                        ? 'text-2xl lg:text-3xl'
                        : 'text-xl lg:text-2xl text-gray-200'
                  }`}
                >
                  {entry.name}
                </span>
              </div>

              {/* Score */}
              <div
                className={`font-mono font-bold tabular-nums ${
                  entry.rank === 1
                    ? 'text-3xl lg:text-4xl text-[#FFBB38]'
                    : entry.rank <= 3
                      ? 'text-2xl lg:text-3xl text-gray-200'
                      : 'text-xl lg:text-2xl text-gray-400'
                }`}
              >
                {formatScore(entry.score, data.workout.scoring_type)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-8 text-center text-gray-600 text-sm">
        Auto-refreshes every 30 seconds
      </div>
    </div>
  );
}
